// src/components/resources/tools/generators/SubtitleOverlay/SubtitleOverlayGenerator.tsx

'use client'

import { useState, useCallback, useDeferredValue, useMemo } from 'react'
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  CheckIcon,
  EyeSlashIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { subtitleOverlayGenerator } from '~/lib/resources/data/generators/subtitleOverlayGenerator'
import {
  type GeneratorField,
  type Condition,
  isEnhancedValidation
} from '~/lib/resources/types/tools/generator'
import { toast } from 'sonner'
import { useStepScrollReset } from '~/components/resources/tools/generators/shared/useStepScrollReset'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'

import { clientLogger, logClientError } from '~/lib/logger-client'
import { FormField } from './FormField'
import { SubtitleOverlayPreview } from './SubtitleOverlayPreview'
import { SubtitleOverlayOutput } from './SubtitleOverlayOutput'

/**
 * Main subtitle overlay generator component
 * Provides a complete interface for creating subtitle overlays with preview and download functionality
 */
export function SubtitleOverlayGenerator(): JSX.Element {
  return <SubtitleOverlayGeneratorContent />
}

/**
 * Content component that manages the generator state and UI
 * Handles form data, validation, step navigation, and generation logic
 */
function SubtitleOverlayGeneratorContent(): JSX.Element {
  // State for UI control
  const [previewState, setPreviewState] = useState<'expanded' | 'minimized' | 'hidden'>('expanded')
  const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)
  const [isGenerated, setIsGenerated] = useState(false)
  const [formData, setFormData] = useState<Record<string, unknown>>({})

  // Render the subtitle preview(s) from a `useDeferredValue` snapshot so typing
  // subtitle copy commits at the form fields immediately (they read live
  // `formData`) while the styled overlay preview re-renders at a lower priority
  // — the same boundary the mockup generators use to shorten the long
  // interaction task behind the generator pages' mobile INP. PNG export waits
  // for the snapshot to converge before capture.
  const deferredFormData = useDeferredValue(formData)
  const [currentStep, setCurrentStep] = useState(0)

  // Initialize engagement timers - memoized for stable references and optimal performance
  // These track user engagement without causing re-renders or performance issues
  const [getTotalEngagementMs, _getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])

  // Toggle preview state
  const togglePreview = useCallback((): void => {
    setPreviewState(prev => {
      if (prev === 'expanded') return 'minimized'
      if (prev === 'minimized') return 'expanded'
      return 'expanded'
    })
  }, [])

  // Hide preview completely
  const hidePreview = useCallback((): void => {
    setPreviewState('hidden')
  }, [])

  // Show preview from hidden state
  const showPreview = useCallback((): void => {
    setPreviewState('expanded')
  }, [])

  // Get steps from subtitleOverlayGenerator config
  const steps = subtitleOverlayGenerator.config.steps
  const currentStepConfig = steps[currentStep]
  // Reset scroll position whenever the wizard advances so the next step starts
  // at the top of the viewport instead of mid-form on mobile devices.
  const stepContainerRef = useStepScrollReset<HTMLDivElement>(currentStep)

  const resolveValidationMessage = useCallback((message: string | undefined, fallback: string): string => {
    // Matching the previous `||` behaviour requires ignoring blank custom messages so legacy configs still fall back correctly.
    if (typeof message === 'string' && message.trim() !== '') {
      return message
    }
    return fallback
  }, [])

  /**
   * Evaluates a condition against form data
   * @param condition - The condition to evaluate
   * @param data - The form data to evaluate against
   * @returns Whether the condition is met
   */
  const evaluateCondition = useCallback((condition: Condition, data: Record<string, unknown>): boolean => {
    if (!condition) return true
    
    const fieldValue = data[condition.field]
    const conditionValue = condition.value

    switch (condition.operator) {
      case 'equals':
        return fieldValue === conditionValue
      case 'notEquals':
        return fieldValue !== conditionValue
      case 'contains':
        return typeof fieldValue === 'string' && fieldValue.includes(String(conditionValue))
      case 'notContains':
        return typeof fieldValue === 'string' && !fieldValue.includes(String(conditionValue))
      case 'greaterThan':
        return Number(fieldValue) > Number(conditionValue)
      case 'lessThan':
        return Number(fieldValue) < Number(conditionValue)
      case 'greaterThanOrEquals':
        return Number(fieldValue) >= Number(conditionValue)
      case 'lessThanOrEquals':
        return Number(fieldValue) <= Number(conditionValue)
      case 'isEmpty':
        return !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '')
      case 'isNotEmpty':
        return Boolean(fieldValue && (typeof fieldValue !== 'string' || fieldValue.trim() !== ''))
      case 'exists':
        return fieldValue !== undefined && fieldValue !== null
      case 'notExists':
        return fieldValue === undefined || fieldValue === null
      default:
        return true
    }
  }, [])

  /**
   * Checks if a field should be shown based on conditions
   */
  const shouldShowField = useCallback((field: GeneratorField): boolean => {
    if (!field.showIf) return true
    return evaluateCondition(field.showIf, formData)
  }, [formData, evaluateCondition])

  /**
   * Checks if a field is required based on conditions
   */
  const isFieldRequired = useCallback((field: GeneratorField): boolean => {
    if (field.validation?.required) return true
    if (field.validation && isEnhancedValidation(field.validation) && field.validation.requiredIf) {
      return evaluateCondition(field.validation.requiredIf, formData)
    }
    return false
  }, [formData, evaluateCondition])

  /**
   * Updates form data for a specific field
   */
  const updateField = useCallback((fieldId: string, value: unknown): void => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }))
    
    // Track image uploads - when backgroundImage field is updated with a non-empty value
    // This provides insights into how many users upload custom images vs using defaults
    if (fieldId === 'backgroundImage' && value && typeof value === 'string' && value.length > 0) {
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: 'subtitle-overlay-generator',
        engagementMs: getTotalEngagementMs(),
        params: {
          tool_type: 'generator',
          interaction_type: 'image_upload'
        }
      })
    }
    
    // Clear validation error for this field
    setLocalValidationErrors(prev => {
      const newErrors = { ...prev }
      // Use bracket notation to avoid dynamic delete
      if (fieldId in newErrors) {
        const { [fieldId]: _, ...rest } = newErrors
        return rest
      }
      return newErrors
    })
  }, [getTotalEngagementMs])

  /**
   * Validates the current step's fields
   * @returns Whether the step is valid
   */
  const validateStep = useCallback((): boolean => {
    const errors: Record<string, string> = {}
    let isValid = true

    currentStepConfig?.fields.forEach(field => {
      if (!shouldShowField(field)) return

      const isRequired = isFieldRequired(field)
      const value = formData[field.id] ?? field.defaultValue

      if (isRequired && (!value || (typeof value === 'string' && value.trim() === ''))) {
        errors[field.id] = `${field.label} is required`
        isValid = false
      }

      // Enhanced validation based on field type and validation config
      if (value && field.validation) {
        const validation = field.validation
        
        // Handle enhanced validation
        if (isEnhancedValidation(validation)) {
          // Enhanced min validation
          if (validation.enhancedMin && Number(value) < validation.enhancedMin.value) {
            errors[field.id] = resolveValidationMessage(
              validation.enhancedMin.message,
              `${field.label} must be at least ${validation.enhancedMin.value}`
            )
            isValid = false
          }

          // Enhanced max validation
          if (validation.enhancedMax && Number(value) > validation.enhancedMax.value) {
            errors[field.id] = resolveValidationMessage(
              validation.enhancedMax.message,
              `${field.label} must be at most ${validation.enhancedMax.value}`
            )
            isValid = false
          }

          // Step constraint validation
          if (validation.stepConstraint) {
            const numValue = Number(value)
            const step = validation.stepConstraint.value
            // Use a small epsilon for floating point comparison
            const epsilon = 1e-10
            const remainder = Math.abs(numValue % step)
            if (remainder > epsilon && Math.abs(remainder - step) > epsilon) {
              errors[field.id] = resolveValidationMessage(
                validation.stepConstraint.message,
                `${field.label} must be a multiple of ${step}`
              )
              isValid = false
            }
          }
        } else {
          // Legacy validation - cast to ValidationRules to access min/max
          const legacyValidation = validation as { min?: number; max?: number; step?: number }
          if (legacyValidation.min && Number(value) < legacyValidation.min) {
            errors[field.id] = `${field.label} must be at least ${legacyValidation.min}`
            isValid = false
          }
          if (legacyValidation.max && Number(value) > legacyValidation.max) {
            errors[field.id] = `${field.label} must be at most ${legacyValidation.max}`
            isValid = false
          }
        }
      }
    })

    setLocalValidationErrors(errors)
    setShowValidationErrors(true)
    return isValid
  }, [currentStepConfig, formData, shouldShowField, isFieldRequired, resolveValidationMessage])

  /**
   * Navigates to the next step
   */
  const nextStep = useCallback((): void => {
    if (validateStep()) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1)
        setShowValidationErrors(false)
      }
    }
  }, [currentStep, steps.length, validateStep])

  /**
   * Navigates to the previous step
   */
  const prevStep = useCallback((): void => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
      setShowValidationErrors(false)
    }
  }, [currentStep])

  /**
   * Generates the subtitle overlay by validating the current step
   */
  const generateOverlay = useCallback((): void => {
    if (validateStep()) {
      setIsGenerated(true)
      // Track generation - zero cost, no params needed
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: 'subtitle-overlay-generator',
        params: { tool_type: 'generator', interaction_type: 'generate' }
      })
    }
  }, [validateStep])

  /**
   * Resets the generator to initial state
   */
  const resetGenerator = useCallback((): void => {
    setFormData({})
    setCurrentStep(0)
    setIsGenerated(false)
    setShowValidationErrors(false)
    setLocalValidationErrors({})
    // Track reset - zero cost
    sendResourceEvent('interact', {
      resourceType: 'tool',
      resourceSlug: 'subtitle-overlay-generator',
      params: { tool_type: 'generator', interaction_type: 'reset' }
    })
  }, [])

  /**
   * Resolves the preview element, waiting briefly if not immediately available.
   */
  const resolvePreviewElement = useCallback(async (): Promise<HTMLElement | null> => {
    const preview = document.getElementById('subtitle-overlay-final-preview')
    if (preview) {
      return preview
    }
    await new Promise((resolve) => setTimeout(resolve, 100))
    return document.getElementById('subtitle-overlay-final-preview')
  }, [])

  /**
   * Downloads the generated overlay as PNG
   */
  const downloadOverlay = useCallback(async (): Promise<void> => {
    try {
      const html2canvas = (await import('html2canvas')).default

      const element = await resolvePreviewElement()
      if (element) {
        // `#subtitle-overlay-final-preview` lives in <SubtitleOverlayOutput>, which
        // renders from LIVE `formData` (not the deferred sidebar snapshot), so the
        // captured DOM already matches the controls — no stale-snapshot wait is
        // needed here. A short settle covers any pending image/paint.
        await new Promise(resolve => setTimeout(resolve, 200))

        const canvas = await html2canvas(element, {
          backgroundColor: null, // Transparent background
          scale: 2,
          useCORS: true,
          allowTaint: true,
          foreignObjectRendering: false,
        })

        // Check if canvas has content
        if (canvas.width === 0 || canvas.height === 0) {
          clientLogger.error('Canvas is empty', { component: 'SubtitleOverlayGenerator' })
          toast.error('Failed to generate image', {
            description: 'The preview could not be captured. Please try again.'
          })
          return
        }

        // Convert to blob and download
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `subtitle-overlay-mockup-${new Date().toISOString().split('T')[0]}.png`
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)

            // Show success toast
            toast.success('Download started', {
              description: 'Your subtitle overlay image is being downloaded.'
            })
          } else {
            throw new Error('Failed to create blob from canvas')
          }
        }, 'image/png', 1.0)
      } else {
        clientLogger.error('Preview element not found', { component: 'SubtitleOverlayGenerator' })
        toast.error('Preview not available', {
          description: 'Please ensure the preview is visible before downloading.'
        })
      }
    } catch (error) {
      logClientError(error, { component: 'SubtitleOverlayGenerator', context: 'Failed to generate PNG' })
      toast.error('Failed to generate PNG', {
        description: 'An error occurred while creating the image. Please try again.'
      })
    }
  }, [resolvePreviewElement])

  // Memoize the sidebar preview ELEMENT so the per-keystroke urgent re-render
  // (deferredFormData referentially unchanged) reuses it and React bails out of
  // re-rendering the styled subtitle preview — it renders only on the low-priority
  // deferred pass. Without this, useDeferredValue double-renders it and *raises* INP.
  const subtitlePreview = useMemo(
    () => <SubtitleOverlayPreview formData={deferredFormData} />,
    [deferredFormData],
  )

  return (
    <div className="space-y-10">
      <div
        ref={stepContainerRef}
        className={clsx(
          'grid gap-8 transition-all duration-300',
          previewState === 'hidden' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'
        )}
      >
        {/* Main Content */}
        <div className={clsx(
          "transition-all duration-300",
          previewState === 'hidden' ? "lg:col-span-1" : "lg:col-span-2"
        )}>
          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Step {currentStep + 1} of {steps.length}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div 
                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Step Content */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {currentStepConfig?.title}
              </h2>
              <p className="mt-2 text-gray-600 dark:text-gray-400">
                {currentStepConfig?.description}
              </p>
            </div>

            <div className="space-y-6">
              {currentStepConfig?.fields.map((field) => {
                if (!shouldShowField(field)) return null

                const isRequired = isFieldRequired(field)
                const hasError = showValidationErrors && localValidationErrors[field.id]
                const value = formData[field.id] ?? field.defaultValue

                return (
                  <div key={field.id} className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      {field.label}
                      {isRequired && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {field.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {field.description}
                      </p>
                    )}
                    
                    <FormField
                      field={field}
                      showValidationError={!!hasError}
                      isRequired={isRequired}
                      value={value}
                      onChange={(newValue) => updateField(field.id, newValue)}
                      setLocalValidationErrors={setLocalValidationErrors}
                    />
                    
                    {hasError && (
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {localValidationErrors[field.id]}
                      </p>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Navigation Buttons */}
            <div className="mt-8 space-y-4">
              {/* Primary Navigation */}
              <div className="flex items-center justify-between">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  className={clsx(
                    'flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md min-w-0',
                    currentStep === 0
                      ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                      : 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                  )}
                >
                  <ChevronLeftIcon className="w-4 h-4 mr-1" />
                  <span className="truncate">Previous</span>
                </button>

                {currentStep === steps.length - 1 ? (
                  <button
                    onClick={generateOverlay}
                    className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md min-w-0"
                  >
                    <CheckIcon className="w-4 h-4 mr-1" />
                    <span className="truncate">Generate</span>
                  </button>
                ) : (
                  <button
                    onClick={nextStep}
                    className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md min-w-0"
                  >
                    <span className="truncate">Next</span>
                    <ChevronRightIcon className="w-4 h-4 ml-1" />
                  </button>
                )}
              </div>

              {/* Preview Controls */}
              <div className="flex items-center justify-center">
                {previewState === 'hidden' ? (
                  <button
                    onClick={showPreview}
                    className="flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 min-w-0"
                  >
                    <EyeIcon className="w-4 h-4 mr-1" />
                    <span className="truncate">Show Preview</span>
                  </button>
                ) : (
                  <button
                    onClick={togglePreview}
                    className={clsx(
                      'flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors min-w-0',
                      previewState === 'expanded'
                        ? 'text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-gray-800'
                        : 'text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10'
                    )}
                  >
                    {previewState === 'expanded' ? (
                      <>
                        <ChevronDownIcon className="w-4 h-4 mr-1" />
                        <span className="truncate">Minimize Preview</span>
                      </>
                    ) : (
                      <>
                        <ChevronUpIcon className="w-4 h-4 mr-1" />
                        <span className="truncate">Expand Preview</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Preview Sidebar */}
        {previewState !== 'hidden' && (
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Preview Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Preview
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={togglePreview}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 min-w-[32px] min-h-[32px] flex items-center justify-center"
                      title={previewState === 'expanded' ? 'Minimize preview' : 'Expand preview'}
                    >
                      {previewState === 'expanded' ? (
                        <ChevronDownIcon className="w-4 h-4" />
                      ) : (
                        <ChevronUpIcon className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={hidePreview}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 min-w-[32px] min-h-[32px] flex items-center justify-center"
                      title="Hide preview"
                    >
                      <EyeSlashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                {/* Preview Content */}
                <div className={clsx(
                  "transition-all duration-300 overflow-hidden",
                  previewState === 'expanded' ? "max-h-none" : "max-h-0"
                )}>
                  <div className="p-4">
                    {subtitlePreview}
                  </div>
                </div>

                {/* Minimized Preview Indicator */}
                {previewState === 'minimized' && (
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>Preview minimized</span>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span className="text-xs">Live</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {isGenerated && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Your Subtitle Overlay
            </h3>
            <div className="flex gap-3">
              <button
                onClick={resetGenerator}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-md"
              >
                Create Another
              </button>
              <button
                onClick={downloadOverlay}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                Download PNG
              </button>
            </div>
          </div>

          <SubtitleOverlayOutput formData={formData} />
        </div>
      )}
    </div>
  )
}
